// frontend/src/pages/Tickets/TicketsList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Tag, Space, Button, Input, Typography, Card, Modal, Dropdown, Menu, Select, Form, Spin, DatePicker } from 'antd';
import {
  SearchOutlined, DownloadOutlined, EyeOutlined,
  FilePdfOutlined, FileWordOutlined, SignatureOutlined, 
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../utils/api';
import './TicketsList.css';
import { useNavigate } from 'react-router-dom';
import { useNotificationApi } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'unassigned': return 'red';
    case 'waiting for support': return 'orange';
    case 'in progress': return 'blue';
    case 'waiting for customer': return 'gold';
    case 'escalated': return 'purple';
    case 'resolved': return 'green';
    case 'closed': return 'default';
    case 'reopened': return 'volcano';
    case 'cancelled': return 'red';
    default: return 'default';
  }
};

// Helper to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high': return 'volcano';
    case 'medium': return 'gold';
    case 'low': return 'lime';
    case 'others': return 'purple';
    default: return 'purple';
  }
};

const TicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortInfo, setSortInfo] = useState({});
  const [activeView, setActiveView] = useState('all'); // 'all', 'my', 'closed', 'to_sign'
  const [initialStatusFilter, setInitialStatusFilter] = useState(null);
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  const { user } = useAuth();
  const [appSettings, setAppSettings] = useState({
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm'
  });

  // States for signing functionality
  const [customerUsers, setCustomerUsers] = useState([]);
  const [loadingCustomerUsers, setLoadingCustomerUsers] = useState(false);
  const [signModalVisible, setSignModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form] = Form.useForm();

  // Use refs to access current state in callbacks without dependencies
  const globalSearchTextRef = useRef(globalSearchText);
  const paginationRef = useRef(pagination);
  const columnFiltersRef = useRef(columnFilters);
  const sortInfoRef = useRef(sortInfo);
  const activeViewRef = useRef(activeView);

  // Update refs when state changes
  useEffect(() => {
    globalSearchTextRef.current = globalSearchText;
    console.log(`[STATE] globalSearchText updated to: "${globalSearchText}"`);
  }, [globalSearchText]);

  useEffect(() => {
    paginationRef.current = pagination;
    console.log(`[STATE] pagination updated to:`, pagination);
  }, [pagination]);

    // READ STATUS FROM URL - Add this useEffect HERE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status && !initialStatusFilter) {
      const decodedStatus = decodeURIComponent(status);
      console.log(`[URL] Found status filter in URL: "${decodedStatus}"`);
      setInitialStatusFilter(decodedStatus);
      
      // Apply the status filter to columnFilters
      setColumnFilters(prev => ({
        ...prev,
        statusName: [decodedStatus]
      }));
      
      // Clear the URL parameter so it doesn't reapply on refresh
      window.history.replaceState({}, '', '/tickets');
    }
  }, [initialStatusFilter]);

  useEffect(() => {
    columnFiltersRef.current = columnFilters;
    console.log(`[STATE] columnFilters updated to:`, columnFilters);
  }, [columnFilters]);

  useEffect(() => {
    sortInfoRef.current = sortInfo;
    console.log(`[STATE] sortInfo updated to:`, sortInfo);
  }, [sortInfo]);

  useEffect(() => {
    activeViewRef.current = activeView;
    console.log(`[STATE] activeView updated to: "${activeView}"`);
  }, [activeView]);

  // Notification Helper Functions
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;
    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Function to fetch application settings
  const fetchAppSettings = useCallback(async () => {
    try {
      const response = await api.get('/api/settings');
      if (response.data && response.data.success) {
        setAppSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('[DEBUG] Error fetching app settings:', error);
    }
  }, []);

  // Helper function for view display names
  const getViewDisplayName = (viewType) => {
    const viewNames = {
      'all': 'All',
      'my': 'My Created / Assigned',
      'closed': 'Closed',
      'to_sign': 'To Sign'
    };
    return viewNames[viewType] || 'All';
  };

  // Helper function to properly format sort parameters
  const getSortByParam = (sortInfo) => {
    if (!sortInfo || !sortInfo.field) {
      return undefined;
    }

    const fieldMapping = {
      // Ticket fields
      'ticketUuid': 'ticketUuid',
      'title': 'title',
      'caseReportedDate': 'caseReportedDate',
      'dueDate': 'dueDate',
      'createdAt': 'createdAt',
      'updatedAt': 'updatedAt',
      
      // Related entity fields
      'statusName': 'statusName',
      'priorityName': 'priorityName',
      'ticketCategoryName': 'ticketCategoryName',
      'creatorFirstName': 'creatorFirstName',
      'assigneeFirstName': 'assigneeFirstName',
      
      // Fallbacks for any other fields
      'summary': 'title',
      'reporter': 'creatorFirstName',
      'assignee': 'assigneeFirstName',
    };

    const field = fieldMapping[sortInfo.field] || sortInfo.field;
    const direction = sortInfo.order === 'ascend' ? 'ASC' : 'DESC';
    
    const sortParam = `${field}:${direction}`;
    console.log(`[SORT] Generated sort parameter: ${sortParam} from sortInfo:`, sortInfo);
    
    return sortParam;
  };

  // Consolidated fetch function for all views - FIXED SORTING VERSION
  const fetchTickets = useCallback(async (viewType = 'all', params = {}, triggeredByAction = false) => {
    console.log(`[FETCH] Starting fetchTickets for view: "${viewType}"`, {
      params,
      currentGlobalSearch: globalSearchTextRef.current,
      currentPagination: paginationRef.current,
      currentColumnFilters: columnFiltersRef.current,
      currentSortInfo: sortInfoRef.current
    });

    setLoadingTickets(true);
    const loadingKey = `fetchTickets_${viewType}`;
    const hideLoading = showLoading(loadingKey, `Loading ${getViewDisplayName(viewType)} tickets...`);

    // Declare variables outside try block so they're available in catch
    let endpoint = '/api/tickets';
    let requestParams = {};

    try {
      endpoint = '/api/tickets';
      requestParams = {
        // Use the search text from params if provided, otherwise use current ref value
        search: params.globalSearchText !== undefined ? params.globalSearchText : globalSearchTextRef.current,
        status: params.columnFilters?.statusName?.join(',') || columnFiltersRef.current?.statusName?.join(','),
        priority: params.columnFilters?.priorityName?.join(',') || columnFiltersRef.current?.priorityName?.join(','),
        ticket_category: params.columnFilters?.ticketCategoryName?.join(',') || columnFiltersRef.current?.ticketCategoryName?.join(','),
        // FIXED: Properly handle sorting from both params and current state
        sortBy: getSortByParam(params.sortInfo || sortInfoRef.current),
        page: params.pagination?.current || paginationRef.current.current,
        pageSize: params.pagination?.pageSize || paginationRef.current.pageSize,
      };

      // Clean up undefined/empty parameters
      Object.keys(requestParams).forEach(key => {
        if (requestParams[key] === undefined || requestParams[key] === '' || requestParams[key] === null) {
          delete requestParams[key];
        }
      });

      // Modify params based on view type
      switch (viewType) {
        case 'my':
          endpoint = '/api/tickets/my-tickets';
          console.log(`[FETCH] Using my-tickets endpoint for "My Created / Assigned" view`);
          break;
        case 'to_sign':
          endpoint = '/api/tickets/my-tickets';
          requestParams.type = 'tickets-to-sign';
          requestParams.status = 'Closed'; // Only show closed tickets for signing
          console.log(`[FETCH] Using my-tickets endpoint for "To Sign" view with type: tickets-to-sign`);
          break;
        case 'closed':
          requestParams.status = 'Closed';
          console.log(`[FETCH] Using all tickets endpoint for "Closed" view with status: Closed`);
          break;
        case 'all':
        default:
          console.log(`[FETCH] Using all tickets endpoint for "All" view`);
          break;
      }

      console.log(`[FETCH] Making API call to: ${endpoint}`, {
        requestParams,
        finalSearchText: requestParams.search,
        finalSortBy: requestParams.sortBy
      });

      const response = await api.get(endpoint, { params: requestParams });
      const responseData = response.data.data;
      const totalCount = response.data.totalCount;

      console.log(`[FETCH] API Response:`, {
        dataCount: Array.isArray(responseData) ? responseData.length : 0,
        totalCount,
        hasData: !!responseData,
        endpoint,
        searchParam: requestParams.search,
        sortParam: requestParams.sortBy
      });

      setTickets(Array.isArray(responseData) ? responseData : []);
      setPagination(prev => ({
        ...prev,
        total: totalCount || 0,
        current: params.pagination?.current || prev.current,
        pageSize: params.pagination?.pageSize || prev.pageSize,
      }));

      hideLoading();
      if (!triggeredByAction) {
        showSuccess('Tickets Loaded', `${getViewDisplayName(viewType)} tickets fetched successfully!`);
      }
    } catch (error) {
      hideLoading();
      console.error(`[FETCH ERROR] Error fetching ${viewType} tickets:`, {
        error: error.response?.data || error.message,
        endpoint,
        requestParams,
        viewType
      });
      showError('Failed to Fetch Tickets', error.response?.data?.message || 'An unexpected error occurred while fetching tickets.');
      setTickets([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoadingTickets(false);
    }
  }, [showLoading, showSuccess, showError]);

  // Signing functionality
  const fetchCustomerUsers = useCallback(async () => {
      setLoadingCustomerUsers(true);
      try {
          const response = await api.get('/api/users/customers');
          if (response.data.success) {
              // Filter only Customer Standard users
              const customerStandardUsers = response.data.data.filter(
                  user => user.roleName === 'Customer Standard'
              );
              setCustomerUsers(customerStandardUsers);
          }
      } catch (error) {
          console.error('Error fetching customer users:', error);
          showError('Fetch Error', 'Failed to load customer users for signing');
      } finally {
          setLoadingCustomerUsers(false);
      }
  }, [showError]);

  const handleSign = async (ticketId, nextSignerId = null) => {
    const loadingKey = `signTicket_${ticketId}`;
    const hideLoading = showLoading(loadingKey, 'Signing ticket...');

    try {
      const payload = nextSignerId ? { nextSignerId } : {};
      const response = await api.post(`/api/tickets/${ticketId}/sign`, payload);
      
      hideLoading();
      showSuccess('Ticket Signed', 'Ticket has been successfully signed.');
      
      // Refresh current view
      await fetchTickets(activeView, {}, true);
      
      setSignModalVisible(false);
      setSelectedTicket(null);
      form.resetFields();
      
    } catch (error) {
      hideLoading();
      let errorMessage = 'An unexpected error occurred while signing the ticket.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showError('Sign Error', errorMessage);
    }
  };

  const openSignModal = (ticket) => {
    setSelectedTicket(ticket);
    setSignModalVisible(true);
    fetchCustomerUsers();
  };

  const handleDirectSign = (ticketId) => {
    handleSign(ticketId);
  };

  const handleModalSign = () => {
    form.validateFields().then(values => {
      if (selectedTicket && values.customerUser) {
        handleSign(selectedTicket.id, values.customerUser);
      }
    });
  };

  const needsCustomerSelection = () => {
    const userRole = user?.roleName;
    return [
      'Internal Staff Supervisor',
      'Partner Standard',
      'Partner Supervisor',
      'System Admin'
    ].includes(userRole);
  };

  // FIXED: Handle global search with proper state management
  const handleGlobalSearch = (value) => {
    console.log(`[SEARCH] handleGlobalSearch triggered with value: "${value}"`);
    console.log(`[SEARCH] Current active view: "${activeView}"`);
    
    // Update state
    setGlobalSearchText(value);
    
    // Reset to first page when searching
    setPagination(prev => ({ ...prev, current: 1 }));
    
    // Prepare search parameters - use the value directly since state update is async
    const searchParams = {
      globalSearchText: value, // Use the current value directly
      pagination: { current: 1, pageSize: pagination.pageSize }, // Always reset to page 1
      columnFilters,
      sortInfo
    };
    
    console.log(`[SEARCH] Calling fetchTickets with:`, searchParams);
    
    // Trigger fetch with the current search value
    fetchTickets(activeView, searchParams, true);
  };

  const handleViewChange = (viewType) => {
    console.log(`[VIEW] Changing view from "${activeView}" to "${viewType}"`);
    
    setActiveView(viewType);
    setGlobalSearchText('');
    setColumnFilters({});
    setSortInfo({});
    setPagination(prev => ({ ...prev, current: 1 }));
    
    // Fetch tickets for the new view
    fetchTickets(viewType, {
      pagination: { current: 1, pageSize: pagination.pageSize },
      globalSearchText: '',
      columnFilters: {},
      sortInfo: {}
    }, true);
  };

  // Handles table changes (pagination, sorting, filtering) - FIXED VERSION
  const handleTableChange = (newPagination, filters, sorter) => {
    console.log(`[TABLE] Table change event:`, {
      pagination: newPagination,
      filters,
      sorter
    });

    const mappedFilters = {};
    for (const key in filters) {
        if (filters[key] && filters[key].length > 0) {
            const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            mappedFilters[camelCaseKey] = filters[key];
        }
    }
    
    // Update filters
    setColumnFilters(mappedFilters);

    // Update sorting - handle both single and multiple sorting
    let newSortInfo = {};
    if (sorter && sorter.field) {
      newSortInfo = {
        field: sorter.field,
        order: sorter.order
      };
    } else {
      newSortInfo = {};
    }
    
    console.log(`[TABLE] Setting new sort info:`, newSortInfo);
    setSortInfo(newSortInfo);

    // Update pagination
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));

    // Trigger immediate fetch with all updated parameters
    const fetchParams = {
      pagination: { current: newPagination.current, pageSize: newPagination.pageSize },
      globalSearchText: globalSearchText,
      columnFilters: mappedFilters,
      sortInfo: newSortInfo
    };
    
    console.log(`[TABLE] Triggering fetch with params:`, fetchParams);
    fetchTickets(activeView, fetchParams, true);
  };

  // Initial fetch on component mount and when view changes
  useEffect(() => {
    console.log(`[EFFECT] Initial fetch or view change detected: "${activeView}"`);
    fetchTickets(activeView, {
      pagination: { current: 1, pageSize: pagination.pageSize },
      globalSearchText,
      columnFilters,
      sortInfo
    });
    fetchAppSettings();
  }, [activeView]); // Only depend on activeView

  useEffect(() => {
    if (initialStatusFilter) {
      console.log(`[EFFECT] Initial status filter set to "${initialStatusFilter}", refetching tickets...`);
      fetchTickets(activeView, {
        pagination: { current: 1, pageSize: pagination.pageSize },
        globalSearchText: '',
        columnFilters: { statusName: [initialStatusFilter] },
        sortInfo: {}
      }, true);
    }
  }, [initialStatusFilter]); // Only run when initialStatusFilter is set

  // Effect for table changes (pagination, sorting, filtering) - FIXED VERSION
  useEffect(() => {
    // Only trigger fetch if we have meaningful changes (not initial load)
    const hasPaginationChange = pagination.current > 1 || pagination.pageSize !== 10;
    const hasSortingChange = Object.keys(sortInfo).length > 0;
    const hasFilteringChange = Object.keys(columnFilters).length > 0;
    
    if (hasPaginationChange || hasSortingChange || hasFilteringChange) {
      console.log(`[EFFECT] Table change detected - triggering fetch`, {
        hasPaginationChange,
        hasSortingChange,
        hasFilteringChange,
        sortInfo,
        columnFilters
      });
      
      fetchTickets(activeView, {
        pagination: { current: pagination.current, pageSize: pagination.pageSize },
        globalSearchText,
        columnFilters,
        sortInfo
      }, true);
    }
  }, [pagination.current, pagination.pageSize, sortInfo, columnFilters]);

  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const handleManualRefresh = () => {
    console.log(`[REFRESH] Manual refresh triggered for view: "${activeView}"`);
    fetchTickets(activeView, {
      pagination: { current: pagination.current, pageSize: pagination.pageSize },
      globalSearchText,
      columnFilters,
      sortInfo
    }, true);
    showSuccess('Refreshed', 'Ticket list refreshed successfully.');
  };

  // Handle Download (PDF or DOCX)
  const handleDownload = async (ticketUuid, format) => {
    const loadingKey = `download${format.toUpperCase()}Loading`;
    const hideLoading = showLoading(loadingKey, `Generating ${format.toUpperCase()}...`);

    try {
      const response = await api.get(`/api/reports/tickets/${ticketUuid}/borang-aduan`, {
        params: { format },
        responseType: 'blob',
        timeout: 60000,
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `BorangAduan_${ticketUuid}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8''|\w*')?([^"'\r\n]+)['"]?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      const contentType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess(
        `${format.toUpperCase()} Generated`,
        `Borang Aduan for ticket ${ticketUuid} downloaded successfully!`,
        `download${format.toUpperCase()}Success`
      );
    } catch (error) {
      hideLoading();
      let errorMessage = `An unexpected error occurred while generating the ${format.toUpperCase()}.`;
      if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (parseError) {
          errorMessage = errorText || errorMessage;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showError(
        `Failed to Download ${format.toUpperCase()}`,
        errorMessage,
        `download${format.toUpperCase()}Error`
      );
    }
  };

  // Helper for text search columns
  const getColumnSearchProps = (dataIndex, placeholderText) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${placeholderText}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#4299e1' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  // Base columns for all views
  const baseColumns = [
    {
      title: 'TICKET NUMBER',
      dataIndex: 'ticketUuid',
      key: 'ticketUuid',
      render: (text, record) => (
        <a
          href={`/tickets/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            fontWeight: 600, 
            color: '#4299e1',
            fontSize: '13px',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {text}
        </a>
      ),
      ...getColumnSearchProps('ticketUuid', 'ticket number'),
      sorter: true,
      fixed: 'left',
      width: 180,
    },
    {
      title: 'CONTRACT',  // ← NEW COLUMN START
      dataIndex: 'contractName',
      key: 'contract',
      render: (contractName) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: 500 }}>
          {contractName || 'N/A'}
        </Tag>
      ),
      width: 200,
    },  // ← NEW COLUMN END
    {
      title: 'SUMMARY',
      dataIndex: 'title',
      key: 'summary',
      ...getColumnSearchProps('title', 'summary'),
      sorter: true,
      ellipsis: true,
      width: 300,
      render: (text) => (
        <Text style={{ fontSize: '14px', lineHeight: '1.4', fontWeight: 500 }}>{text}</Text>
      )
    },
    {
      title: 'REPORTER',
      dataIndex: 'creatorFirstName',
      key: 'reporter',
      render: (text, record) => (
        <div style={{ fontWeight: 500, fontSize: '14px' }}>
          {`${record.creatorFirstName || ''} ${record.creatorLastName || ''}`.trim()}
        </div>
      ),
      ...getColumnSearchProps('creatorFirstName', 'reporter'),
      sorter: true,
      width: 180,
    },
    {
      title: 'ASSIGNEE',
      dataIndex: 'assigneeFirstName',
      key: 'assignee',
      render: (text, record) => (
        <div style={{ fontWeight: 500, fontSize: '14px' }}>
          {record.assigneeFirstName ? 
            `${record.assigneeFirstName || ''} ${record.assigneeLastName || ''}`.trim() : 
            <Tag color="default">UNASSIGNED</Tag>
          }
        </div>
      ),
      ...getColumnSearchProps('assigneeFirstName', 'assignee'),
      sorter: true,
      width: 180,
    },
    {
      title: 'STATUS',
      dataIndex: 'statusName',
      key: 'statusName',
      filters: activeView !== 'to_sign' ? [
        { text: 'Unassigned', value: 'Unassigned' },
        { text: 'Waiting For Support', value: 'Waiting For Support' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Waiting For Customer', value: 'Waiting For Customer' },
        { text: 'Escalated', value: 'Escalated' },
        { text: 'Resolved', value: 'Resolved' },
        { text: 'Closed', value: 'Closed' },
        { text: 'Reopened', value: 'Reopened' },
        { text: 'Cancelled', value: 'Cancelled' },
      ] : null,
      onFilter: activeView !== 'to_sign' ? (value, record) => record.statusName?.toLowerCase().includes(value.toLowerCase()) : null,
      render: (status) => (
        <Tag color={getStatusColor(status)} key={status}>
          {status?.toUpperCase()}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'PRIORITY',
      dataIndex: 'priorityName',
      key: 'priorityName',
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
        { text: 'Others', value: 'Others' },
      ],
      onFilter: (value, record) => record.priorityName?.toLowerCase().includes(value.toLowerCase()),
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} key={priority}>
          {priority?.toUpperCase()}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'REPORTED DATE',
      dataIndex: 'caseReportedDate',
      key: 'caseReportedDate',
      sorter: true,
      render: (text) => (
        <div style={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
          {text ? moment(text).format(`${appSettings.dateFormat} ${appSettings.timeFormat}`) : 'N/A'}
        </div>
      ),
      // Add filterDropdown for date range selection
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        // FIX: Safely parse the selectedKeys
        let startDate = null;
        let endDate = null;
        
        try {
          if (selectedKeys && selectedKeys[0]) {
            const parsed = JSON.parse(selectedKeys[0]);
            if (Array.isArray(parsed)) {
              startDate = parsed[0];
              endDate = parsed[1];
            }
          }
        } catch (error) {
          console.error('Error parsing date filter:', error);
        }
        
        const handleDateChange = (dates) => {
          if (dates) {
            const [start, end] = dates;
            const startStr = start ? start.toISOString() : null;
            const endStr = end ? end.toISOString() : null;
            setSelectedKeys([JSON.stringify([startStr, endStr])]);
          } else {
            setSelectedKeys([]);
          }
        };
        
        const handleFilter = () => {
          confirm();
        };
        
        const handleReset = () => {
          setSelectedKeys([]);
          clearFilters();
        };
        
        // FIX: Properly handle the value for RangePicker
        const rangeValue = startDate && endDate 
          ? [moment(startDate), moment(endDate)]
          : startDate 
            ? [moment(startDate), null]
            : endDate
              ? [null, moment(endDate)]
              : null;
        
        return (
          <div style={{ padding: 8, width: 320 }}>
            <div style={{ marginBottom: 8 }}>
              <DatePicker.RangePicker
                value={rangeValue}
                onChange={handleDateChange}
                format={appSettings.dateFormat}
                style={{ width: '100%' }}
                placeholder={['Start Date', 'End Date']}
                allowClear={true}
              />
            </div>
            <Space>
              <Button
                type="primary"
                onClick={handleFilter}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button 
                onClick={handleReset} 
                size="small" 
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#4299e1' : undefined }} />
      ),
      onFilter: (value, record) => {
        if (!value) return true;
        
        try {
          const [startDateStr, endDateStr] = JSON.parse(value);
          const recordDate = moment(record.caseReportedDate);
          
          // If no dates selected, return all
          if (!startDateStr && !endDateStr) return true;
          
          const startDate = startDateStr ? moment(startDateStr) : null;
          const endDate = endDateStr ? moment(endDateStr) : null;
          
          // Single date filtering
          if (startDate && !endDate) {
            return recordDate.isSameOrAfter(startDate, 'day');
          }
          if (!startDate && endDate) {
            return recordDate.isSameOrBefore(endDate, 'day');
          }
          
          // Date range filtering
          if (startDate && endDate) {
            return recordDate.isBetween(startDate, endDate, 'day', '[]');
          }
          
          return true;
        } catch (error) {
          console.error('Error filtering dates:', error);
          return true;
        }
      },
      width: 200,
    },
  ];

  // Additional columns for "To Sign" view
  const signingColumns = [
    {
      title: 'SUPERVISOR SIGNED',
      dataIndex: 'supervisorSignedAt',
      key: 'supervisorSignedAt',
      render: (text) => text ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>
      ),
      width: 150,
    },
    {
      title: 'CUSTOMER SIGNED',
      dataIndex: 'customerStandardSignedAt',
      key: 'customerStandardSignedAt',
      render: (text) => text ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>
      ),
      width: 150,
    },
  ];

  // Action columns
  const getActionColumn = () => ({
    title: 'ACTIONS',
    key: 'actions',
    render: (_, record) => (
      <Space size="small">
        <Button 
          type="link" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewTicket(record.id)}
          style={{ 
            padding: '6px 12px', 
            fontSize: '13px',
            height: 'auto',
            fontWeight: 600
          }}
        >
          View
        </Button>
        <Dropdown
          overlay={
            <Menu
              onClick={({ key }) => handleDownload(record.ticketUuid, key)}
              items={[
                {
                  key: 'pdf',
                  label: 'Download as PDF',
                  icon: <FilePdfOutlined />,
                },
                {
                  key: 'docx',
                  label: 'Download as Word',
                  icon: <FileWordOutlined />,
                },
              ]}
            />
          }
          trigger={['click']}
        >
          <Button 
            type="link" 
            icon={<DownloadOutlined />}
            style={{ 
              padding: '6px 12px', 
              fontSize: '13px',
              height: 'auto',
              fontWeight: 600
            }}
          >
            Export
          </Button>
        </Dropdown>
        {activeView === 'to_sign' && (
          <Button 
            type="link" 
            icon={<SignatureOutlined />} 
            onClick={() => needsCustomerSelection() ? openSignModal(record) : handleDirectSign(record.id)}
            style={{ 
              padding: '6px 12px', 
              fontSize: '13px',
              height: 'auto',
              fontWeight: 600
            }}
          >
            Sign
          </Button>
        )}
      </Space>
    ),
    fixed: 'right',
    width: activeView === 'to_sign' ? 220 : 180,
  });

  // Build columns based on active view
  const getColumns = () => {
    let columns = [...baseColumns];
    
    if (activeView === 'to_sign') {
      columns = [...columns, ...signingColumns];
    }
    
    columns.push(getActionColumn());
    return columns;
  };

  return (
    <Card className="tickets-list-page-card">
      <div className="tickets-list-header">
        <Title level={2} className="tickets-list-title">Ticket Management System</Title>
        <Text type="secondary" className="tickets-list-description">
          Comprehensive overview and management of all support tickets across the organization
        </Text>
      </div>

      {/* Sign Modal */}
      <Modal
        title="Select Customer User for Next Signature"
        open={signModalVisible}
        onCancel={() => {
          setSignModalVisible(false);
          setSelectedTicket(null);
          form.resetFields();
        }}
        onOk={handleModalSign}
        okText="Sign and Forward to Customer"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="customerUser"
            label="Select Customer User"
            rules={[{ required: true, message: 'Please select a customer user to forward the ticket to' }]}
          >
            <Select
              placeholder="Search and select a customer user..."
              loading={loadingCustomerUsers}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={loadingCustomerUsers ? <Spin size="small" /> : "No customer users found"}
            >
              {customerUsers.map(user => (
                <Option key={user.id} value={user.id}>
                  {`${user.firstName} ${user.lastName} - ${user.organizationName || 'No Organization'} (${user.roleName})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Text type="secondary">
            After you sign, the ticket will be forwarded to the selected customer user for their signature.
          </Text>
        </Form>
      </Modal>

      {/* View Selection and Filters */}
      <div className="tickets-table-filters-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <Select
            value={activeView}
            onChange={handleViewChange}
            style={{ width: 200 }}
            size="large"
          >
            <Option value="all">All Tickets</Option>
            <Option value="my">My Created / Assigned Tickets</Option>
            <Option value="closed">Closed Tickets</Option>
            <Option value="to_sign">Tickets To Sign</Option>
          </Select>

          <Search
            placeholder={`Search ${getViewDisplayName(activeView).toLowerCase()} tickets...`}
            enterButton="Search"
            value={globalSearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            onSearch={handleGlobalSearch}
            onPressEnter={(e) => {
              e.preventDefault();
              handleGlobalSearch(globalSearchText);
            }}
            style={{ width: 400 }}
            className="tickets-global-search"
            size="large"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="tickets-stats-counter">
            {pagination.total} {getViewDisplayName(activeView)} Tickets
          </div>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleManualRefresh}
            size="large"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="tickets-table-container">
        <Table
          columns={getColumns()}
          dataSource={tickets}
          rowKey="id"
          pagination={pagination}
          loading={loadingTickets}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          className="tickets-ant-table"
          size="middle"
        />
      </div>
    </Card>
  );
};

export default TicketsList;